import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Video, Loader } from "lucide-react";
import VideoRoom from "../components/VideoRoom";
import { mentorshipApi, showError } from "../lib";
import { useAuth } from "../context/AuthContext";

interface Session {
  _id: string;
  mentor: { user?: { firstName?: string; lastName?: string }; firstName?: string; lastName?: string };
  student: { firstName?: string; lastName?: string };
  scheduledAt: string;
  status: string;
  topic: string;
  meetingLink?: string;
  price: number;
}

const VideoRoomPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) {
        showError("Invalid session ID");
        navigate(-1);
        return;
      }

      try {
        setLoading(true);
        const res = await mentorshipApi.getSessionDetails(sessionId);
        if (res.success) {
          setSession(res.data);
        } else {
          showError("Session not found");
          navigate(-1);
        }
      } catch (err) {
        console.error("Failed to fetch session:", err);
        showError("Failed to load session");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, navigate]);

  const displayName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "User"
    : "Guest";

  const handleClose = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-sm text-gray-400">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Video className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-white font-medium mb-2">Session not found</p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mx-auto px-4 py-2 text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Detect platform from meetingLink
  const isZoom = session.meetingLink && session.meetingLink.includes("zoom.us");
  const isJitsi = session.meetingLink && session.meetingLink.includes("meet.jit.si");

  return (
    <VideoRoom
      roomName={session._id}
      displayName={displayName}
      onClose={handleClose}
      meetingLink={session.meetingLink}
      platform={isZoom ? "zoom" : isJitsi ? "jitsi" : "auto"}
    />
  );
};

export default VideoRoomPage;